// useFinanceSettings hook — Task 23
//
// Provides company-wide finance settings state with dirty-tracking and save/reset helpers.
// Follows the same pattern as useBudgetHealthThresholds.

import { useState, useCallback } from 'react';
import {
  type FinanceSettings,
  DEFAULT_FINANCE_SETTINGS,
  getFinanceSettings,
  saveFinanceSettings,
} from '@/lib/finance-settings-storage';
import type { GpCalculationMethod } from '@/lib/gp-calculation';

interface UseFinanceSettingsReturn {
  settings: FinanceSettings;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  updateSetting: <K extends keyof FinanceSettings>(key: K, value: FinanceSettings[K]) => void;
  saveSettings: () => Promise<void>;
  resetToDefaults: () => void;
}

export function useFinanceSettings(): UseFinanceSettingsReturn {
  const [settings, setSettings] = useState<FinanceSettings>(() => getFinanceSettings());
  const [savedSettings, setSavedSettings] = useState<FinanceSettings>(() => getFinanceSettings());
  const [isSaving, setIsSaving] = useState(false);

  const hasUnsavedChanges =
    settings.gpCalculationMethod !== savedSettings.gpCalculationMethod;

  const updateSetting = useCallback(
    <K extends keyof FinanceSettings>(key: K, value: FinanceSettings[K]) => {
      setSettings((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const saveSettings = useCallback(async () => {
    setIsSaving(true);
    try {
      saveFinanceSettings(settings);
      setSavedSettings({ ...settings });
    } finally {
      setIsSaving(false);
    }
  }, [settings]);

  const resetToDefaults = useCallback(() => {
    setSettings({ ...DEFAULT_FINANCE_SETTINGS });
  }, []);

  // Convenience typed helper (not exported on the interface — used internally only)
  void (updateSetting as (key: 'gpCalculationMethod', value: GpCalculationMethod) => void);

  return {
    settings,
    hasUnsavedChanges,
    isSaving,
    updateSetting,
    saveSettings,
    resetToDefaults,
  };
}
